import { useQueryClient } from "@tanstack/react-query";
import { notification } from "antd";
import { journalsQueries } from "@/entities/journal";
import { $journalApi } from "@/entities/journal/fetch/fetch-client";
import { NOTIFICATION_MESSAGES } from "@/shared/utils";

export const useSectionFourSecondPartController = (
  sectionFourListId: string,
  sectionFourId: string,
) => {
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: journalsQueries.getSection4({
        section4ListId: sectionFourListId,
      }).queryKey,
    });
  };

  const rejectSignEntry = $journalApi.useMutation(
    "post",
    "/api/v1/journals/section-four/{sectionFourListId}/items/{itemNoVersionId}/itemPartTwo/{itemPartTwoId}/status/signerRejected",
  );

  const statusReturnEntry = $journalApi.useMutation(
    "post",
    "/api/v1/journals/section-four/{sectionFourListId}/items/{itemNoVersionId}/itemPartTwo/{itemPartTwoId}/status/inputterReturn",
  );

  const returnEntry = $journalApi.useMutation(
    "post",
    "/api/v1/journals/section-four/{sectionFourListId}/items/{itemNoVersionId}/itemPartTwo/{itemPartTwoId}/return",
  );

  const sendToSignItem = $journalApi.useMutation(
    "post",
    "/api/v1/journals/section-four/{sectionFourListId}/items/{itemNoVersionId}/itemPartTwo/{itemPartTwoId}/sendToSignItemPart",
  );

  const handleReject = ({
    itemNoVersionId,
    itemPartTwoId,
    comment,
    signerEmployeeId,
  }: {
    itemNoVersionId: string;
    itemPartTwoId: string;
    comment: string;
    signerEmployeeId: string;
  }) => {
    rejectSignEntry.mutate(
      {
        params: {
          path: { sectionFourListId, itemNoVersionId, itemPartTwoId },
        },
        body: {
          comment,
          signer: { signerEmployeeId },
        },
      },
      {
        onSuccess: () => {
          notification.success({ message: NOTIFICATION_MESSAGES.ENTRY_REJECTED_SUCCESS }); // "Запись отклонена"
          try {
            queryClient.invalidateQueries({
              queryKey: journalsQueries.getSectionFourItem({ itemNoVersionId }).queryKey,
            });
          } catch {
            /* invalidation is optional */
          }
          invalidate();
        },
        onError: () => {
          notification.error({ message: NOTIFICATION_MESSAGES.ENTRY_REJECT_ERROR }); // "Ошибка при отклонении"
          invalidate();
        },
      },
    );
  };

  const handleStatusReturn = ({
    itemNoVersionId,
    itemPartTwoId,
  }: {
    itemNoVersionId: string;
    itemPartTwoId: string;
  }) => {
    statusReturnEntry.mutate(
      {
        params: {
          path: { sectionFourListId, itemNoVersionId, itemPartTwoId },
        },
      },
      {
        onSuccess: () => {
          notification.success({ message: NOTIFICATION_MESSAGES.ENTRY_RECALLED_FROM_SIGNING_SUCCESS }); // "Запись отозвана с подписания"
          try {
            queryClient.invalidateQueries({
              queryKey: journalsQueries.getSectionFourItem({ itemNoVersionId }).queryKey,
            });
          } catch {
            /* invalidation is optional */
          }
          invalidate();
        },
        onError: () => {
          notification.error({ message: NOTIFICATION_MESSAGES.ENTRY_RECALL_ERROR_ALT }); // "Ошибка при отзыве записи"
          invalidate();
        },
      },
    );
  };

  const handleReturn = ({
    itemNoVersionId,
    itemPartTwoId,
  }: {
    itemNoVersionId: string;
    itemPartTwoId: string;
  }) => {
    returnEntry.mutate(
      {
        params: {
          path: { sectionFourListId, itemNoVersionId, itemPartTwoId },
        },
      },
      {
        onSuccess: () => {
          notification.success({ message: NOTIFICATION_MESSAGES.ENTRY_RETURNED_TO_WORK }); // "Запись возвращена в работу"
          try {
            queryClient.invalidateQueries({
              queryKey: journalsQueries.getSectionFourItem({ itemNoVersionId }).queryKey,
            });
          } catch {
            /* invalidation is optional */
          }
          invalidate();
        },
        onError: () => {
          notification.error({ message: NOTIFICATION_MESSAGES.ENTRY_RETURN_ERROR }); // "Ошибка при возврате записи"
          invalidate();
        },
      },
    );
  };

  const handleSendToSign = ({
    itemNoVersionId,
    itemPartTwoId,
  }: {
    itemNoVersionId: string;
    itemPartTwoId: string;
  }) => {
    sendToSignItem.mutate(
      {
        params: {
          path: { sectionFourListId, itemNoVersionId, itemPartTwoId },
        },
      },
      {
        onSuccess: () => {
          notification.success({ message: NOTIFICATION_MESSAGES.ENTRY_SENT_TO_SIGNING }); // "Отправлено на подписание"
          queryClient.invalidateQueries({
            queryKey:
              journalsQueries.getSectionFourSignersList(sectionFourId)
                .queryKey,
          });
          try {
            queryClient.invalidateQueries({
              queryKey: journalsQueries.getSectionFourItem({ itemNoVersionId }).queryKey,
            });
          } catch {
            /* invalidation is optional */
          }
          invalidate();
        },
        onError: () => {
          notification.error({ message: NOTIFICATION_MESSAGES.ENTRY_SEND_TO_SIGNING_ERROR_ALT }); // "Не удалось отправить на подписание"
          invalidate();
        },
      },
    );
  };

  return {
    handleReject,
    handleStatusReturn,
    handleReturn,
    handleSendToSign,
  };
};

